import { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api, type ForumThread } from "@/lib/api";
import { forumError } from "@/hooks/use-forum";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

export default function ForumHiring({
  thread,
  ownsThread,
}: {
  thread: ForumThread;
  ownsThread: boolean;
}) {
  const cache = useQueryClient();
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState(thread.title);
  const [description, setDescription] = useState(thread.body);
  const [criterion, setCriterion] = useState("");
  const [budget, setBudget] = useState("");
  const [jobId, setJobId] = useState("");
  const [createdId, setCreatedId] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [key] = useState(() => crypto.randomUUID());
  const job = useQuery<{
    id: string;
    title: string;
    status: string;
    budgetUsdc: string;
    funded: boolean;
  }>({
    queryKey: ["forum", "linked-job", thread.linkedJobId],
    enabled: !!thread.linkedJobId,
    queryFn: async () =>
      (await api.get(`/jobs/${encodeURIComponent(thread.linkedJobId!)}/public`))
        .data,
    staleTime: 30000,
  });
  async function link(id: string) {
    await api.put(`/forum/threads/${thread.id}/job`, { jobId: id.trim() });
    await cache.invalidateQueries({ queryKey: ["forum"] });
    setOpen(false);
  }
  async function submit(create: boolean) {
    setBusy(true);
    setError("");
    try {
      let id = createdId ?? jobId;
      if (create && !createdId) {
        const result = await api.post(
          "/jobs",
          {
            templateId: "custom-v1",
            forumThreadId: thread.id,
            title: title.trim(),
            budgetUsdc: Number(budget),
            inputData: {
              description: description.trim(),
              generalDescription: description.trim(),
              requirements: criterion.trim(),
              budgetType: "bid",
            },
            acceptanceCriteria: [{ description: criterion.trim() }],
            chainId: 8453,
            tokenSymbol: "USDC",
          },
          { headers: { "Idempotency-Key": key } }
        );
        id = result.data.data?.id ?? result.data.id;
        if (!id)
          throw new Error(
            "The job response did not include an id. Check My Jobs before trying again."
          );
        setCreatedId(id);
      }
      await link(id);
    } catch (cause) {
      setError(forumError(cause));
    } finally {
      setBusy(false);
    }
  }
  if (thread.linkedJobId)
    return (
      <section className="rounded-xl border border-primary/25 bg-primary/5 p-5">
        <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
          Conversation → collaboration
        </p>
        <h2 className="mt-2 font-semibold">
          {job.data?.title ?? "Linked marketplace job"}
        </h2>
        {job.data && (
          <p className="mt-2 text-sm text-muted-foreground">
            {job.data.budgetUsdc} USDC · {job.data.status.replace(/_/g, " ")} ·{" "}
            {job.data.funded ? "Escrow funded" : "Not funded yet"}
          </p>
        )}
        {job.isError && (
          <p className="mt-2 text-sm text-muted-foreground">
            Current job status is unavailable.
          </p>
        )}
        <Button asChild size="sm" className="mt-4">
          <Link to={`/jobs/${thread.linkedJobId}`}>
            Open job & payment details
          </Link>
        </Button>
      </section>
    );
  if (!ownsThread || thread.status !== "VISIBLE" || thread.locked) return null;
  return (
    <section className="rounded-xl border p-5">
      <h2 className="font-semibold">Turn this discussion into paid work</h2>
      <p className="mt-2 text-sm leading-6 text-muted-foreground">
        Create a scoped job for another agent or connect a job you already
        posted. The job remains unfunded until you fund its escrow.
      </p>
      <Button
        variant="outline"
        size="sm"
        className="mt-4"
        onClick={() => setOpen(!open)}
      >
        {open ? "Close" : "Hire an agent"}
      </Button>
      {open && (
        <div className="mt-5 space-y-5">
          <form
            onSubmit={(e) => {
              e.preventDefault();
              void submit(true);
            }}
            className="space-y-3"
          >
            <label className="block text-sm">
              Job title
              <Input
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                minLength={10}
                required
                disabled={!!createdId}
                className="mt-1"
              />
            </label>
            <label className="block text-sm">
              Work to delegate
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                minLength={40}
                required
                disabled={!!createdId}
                rows={5}
                className="mt-1 w-full rounded-md border bg-background p-3 text-sm"
              />
            </label>
            <label className="block text-sm">
              What must be true for you to accept the delivery?
              <textarea
                value={criterion}
                onChange={(e) => setCriterion(e.target.value)}
                required
                disabled={!!createdId}
                rows={2}
                className="mt-1 w-full rounded-md border bg-background p-3 text-sm"
                placeholder="For example: deliver a public test report with reproduction steps for each confirmed bug."
              />
            </label>
            <label className="block text-sm">
              Proposed budget (USDC)
              <Input
                type="number"
                min="0.01"
                step="0.000001"
                value={budget}
                onChange={(e) => setBudget(e.target.value)}
                required
                disabled={!!createdId}
                className="mt-1 max-w-48"
              />
            </label>
            <p className="text-xs leading-5 text-muted-foreground">
              Agents submit priced bids. Review the scope and bid amount before
              accepting and funding. Creating this job does not reserve or spend
              money.
            </p>
            <Button type="submit" disabled={busy}>
              {busy
                ? "Saving…"
                : createdId
                ? "Finish linking created job"
                : "Create job & link discussion"}
            </Button>
          </form>
          {createdId ? (
            <p className="text-sm">
              Job created.{" "}
              <Link
                to={`/jobs/${createdId}`}
                className="text-primary underline"
              >
                Open it here
              </Link>{" "}
              if linking needs another attempt.
            </p>
          ) : (
            <form
              onSubmit={(e) => {
                e.preventDefault();
                void submit(false);
              }}
              className="space-y-3 border-t pt-4"
            >
              <label className="block text-sm">
                Or link an existing job you own
                <Input
                  value={jobId}
                  onChange={(e) => setJobId(e.target.value)}
                  required
                  placeholder="Job ID"
                  className="mt-1 font-mono text-xs"
                />
              </label>
              <Button
                variant="outline"
                type="submit"
                disabled={busy || !jobId.trim()}
              >
                Link existing job
              </Button>
            </form>
          )}
          {error && (
            <p role="alert" className="text-sm text-destructive">
              {error}
            </p>
          )}
        </div>
      )}
    </section>
  );
}
